import {
  useState,
  useEffect,
  useCallback,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";
import { preparePrompt, sendUserPrompt } from "../utils";
import { getBePrompt } from "../../word-suggestion";
import { ChatSettingsType } from "../../settings";
import { RequestStatus, REQUEST_STATUS } from "../../../shared/consts";
import { Message } from "../../message";
import { fetchAutoGeneratedQuestion } from "../../message";
import { fetchReportSuggestions } from "../../report";
import {
  createReportSuggestionMessage,
  createUserMessage,
  createAIMessage,
} from "../../message";
import {
  REPORTS_NULL_MESSAGE,
  REPORTS_ERROR_MESSAGE,
  REPORTS_INTRO_MESSAGE,
  REPORT_INTRO_MESSAGE,
  DEFAULT_AI_MESSAGE,
  GENERIC_ERROR_MESSAGE,
} from "../../message";
import { UrlParamsContext } from "../../../app/context/url-params-context";

export type UseRequestAIProps = {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  resetWordSuggestions: () => void;
  feToBeTermsDict: Record<string, string>;
  chatSettings: ChatSettingsType;
};

export type HandleSendType = (
  autoGenerated?: boolean,
  inputFePrompt?: string,
  inputBePrompt?: string
) => Promise<void>;

export type UseRequestAIResult = {
  messages: Message[];
  handleSend: HandleSendType;
  requestStatus: RequestStatus;
  clearChat: () => void;
  getQuestion: () => Promise<void>;
  requestReportStatus: RequestStatus;
};

const useRequestAI = ({
  input,
  setInput,
  resetWordSuggestions,
  feToBeTermsDict,
  chatSettings,
}: UseRequestAIProps): UseRequestAIResult => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(
    REQUEST_STATUS.IDLE
  );
  const [requestReportStatus, setRequestReportStatus] = useState<RequestStatus>(
    REQUEST_STATUS.IDLE
  );

  const [generatedQuestion, setGeneratedQuestion] = useState<string>("");

  const { functionKey, username } = useContext(UrlParamsContext);

  const getQuestion = useCallback(async () => {
    try {
      const response = await fetchAutoGeneratedQuestion(functionKey);
      if (response.ok) {
        const responseText = await response.text();
        setGeneratedQuestion(responseText);
      } else {
        const errorText = await response.text();
        console.error(errorText);
      }
    } catch (error: any) {
      console.error(error.message);
    }
  }, [functionKey]);

  const getReportSuggestions = useCallback(async (responseId: number) => {
    try {
      setRequestReportStatus(REQUEST_STATUS.LOADING);
      const response = await fetchReportSuggestions(responseId, functionKey);

      let reportMessage = createReportSuggestionMessage(
        REPORTS_NULL_MESSAGE,
        null
      );
      if (response.status === "non-successful") {
        reportMessage = createReportSuggestionMessage(
          REPORTS_ERROR_MESSAGE,
          null
        );
      } else {
        const reports = response.reports;
        if (reports.length > 1) {
          reportMessage = createReportSuggestionMessage(
            REPORTS_INTRO_MESSAGE,
            response.id,
            reports
          );
        } else if (reports.length === 1) {
          reportMessage = createReportSuggestionMessage(
            REPORT_INTRO_MESSAGE,
            response.id,
            reports
          );
        } else {
          reportMessage = createReportSuggestionMessage(
            REPORTS_NULL_MESSAGE,
            null
          );
        }
      }

      setRequestReportStatus(REQUEST_STATUS.SUCCESS);
      setMessages((messages) => [...messages, reportMessage]);
    } catch (error: any) {
      console.error(error.message);
      setRequestReportStatus(REQUEST_STATUS.FAILURE);
      const reportMessage = createReportSuggestionMessage(
        REPORTS_ERROR_MESSAGE,
        null
      );
      setMessages((messages) => [...messages, reportMessage]);
    }
  }, []);

  const handleSend = useCallback<HandleSendType>(
    async (
      autoGenerated: boolean = false,
      inputFePrompt?: string,
      inputBePrompt?: string
    ) => {
      if (requestStatus === REQUEST_STATUS.LOADING) {
        console.log("still loading");
        return;
      }
      const prompt = preparePrompt(
        autoGenerated,
        inputFePrompt ?? input,
        generatedQuestion
      );
      if (!prompt) return;

      const bePrompt = inputBePrompt ?? getBePrompt(prompt, feToBeTermsDict);

      if (autoGenerated) {
        setGeneratedQuestion("");
      } else {
        setInput("");
        resetWordSuggestions();
      }
      setMessages((messages) => [
        ...messages,
        createUserMessage(prompt, autoGenerated),
      ]);
      setRequestStatus(REQUEST_STATUS.LOADING);
      // handle prompts without alphanumeric characters
      if (!/[a-zA-Z0-9]/.test(prompt)) {
        const aiMessages = createAIMessage(DEFAULT_AI_MESSAGE, null);
        setMessages((messages) => [...messages, ...aiMessages]);
        setRequestStatus(REQUEST_STATUS.SUCCESS);
        return;
      }
      // handle valid prompts
      try {
        const response = await sendUserPrompt(
          prompt,
          bePrompt,
          autoGenerated,
          functionKey,
          chatSettings.ChatbotPersonality.selected_option as string,
          chatSettings.Language.selected_option as string,
          username
        );

        console.log("✅ Received response:", response);

        // Create AI message with the response text
        const aiMessages = createAIMessage(response.response_text, response.response_id);
        setMessages((messages) => [...messages, ...aiMessages]);
        setRequestStatus(REQUEST_STATUS.SUCCESS);

        // Get report suggestions if enabled
        if (response.response_id && chatSettings.SuggestReports.selected_option) {
          getReportSuggestions(response.response_id);
        }
      } catch (error: any) {
        const errorMessages = createAIMessage(GENERIC_ERROR_MESSAGE, null, true);
        setMessages((messages) => [...messages, ...errorMessages]);
        setRequestStatus(REQUEST_STATUS.FAILURE);
      }
    },
    [generatedQuestion, input, requestStatus, chatSettings, functionKey]
  );

  useEffect(() => {
    if (generatedQuestion.trim()) {
      handleSend(true);
    }
  }, [generatedQuestion, handleSend]);

  const clearChat = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    handleSend,
    requestStatus,
    clearChat,
    getQuestion,
    requestReportStatus,
  };
};

export default useRequestAI;
